import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowUpRight,
  Download,
  FileText,
  GitMerge,
  ImageOff,
  Loader2,
  Lock,
  Mail,
  MessageSquare,
  Paperclip,
  Plus,
  Save,
  Send,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FileUpload, getImageFilesFromClipboard, type FileUploadHandle } from '@/components/ui/FileUpload';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { SUPPORT_REPLY_TEMPLATES } from './supportReplyTemplates';
import { Select } from '@/components/ui/Select';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { useToast } from '@/hooks/useToast';
import {
  createSupportTicket,
  downloadSupportTicketAttachment,
  fetchSupportTicketAttachmentBlobUrl,
  getSupportTicket,
  getSupportTicketInvestigation,
  investigateSupportTicket,
  buildSupportTicketAssigneeSelectOptions,
  buildSupportTicketParticipantOptions,
  isSupportTicketImageAttachment,
  listSupportTicketAssignees,
  listSupportTicketParticipantCandidates,
  listSupportTickets,
  mergeSupportTicket,
  postSupportTicketInternalNote,
  PROSPERA_RECIPIENT_ID,
  replyToSupportTicket,
  suggestSupportTicketReplies,
  type CreateSupportTicketPayload,
  type MergeSupportTicketPayload,
  type PostInternalNotePayload,
  type ReplyToSupportTicketPayload,
  type SupportTicket,
  type SupportTicketAttachment,
  type SupportTicketPriority,
  type SupportTicketStatus,
  type SupportTicketType,
  updateSupportTicket,
} from '@/services/supportTickets.service';
import { getErrorCode, getErrorMessage } from '@/services/apiClient';
import { SupportTicketPeople } from './SupportTicketPeople';

export const SUPPORT_TICKET_PRIORITY_OPTIONS: Array<{ value: SupportTicketPriority; label: string }> = [
  { value: 'critical', label: 'P1 - Critical' },
  { value: 'high', label: 'P2 - High' },
  { value: 'medium', label: 'P3 - Medium' },
  { value: 'low', label: 'P4 - Low' },
];

export const SUPPORT_TICKET_PRIORITY_DESCRIPTIONS: Record<SupportTicketPriority, string> = {
  critical: 'Complete system outage, business-critical service down.',
  high: 'Severe degradation, core functionality impaired.',
  medium: 'Limited impact, a workaround exists.',
  low: 'Minor issue or general, non-urgent request.',
};

export const SUPPORT_TICKET_TYPE_OPTIONS: Array<{ value: SupportTicketType; label: string }> = [
  { value: 'bug', label: 'Bug' },
  { value: 'account', label: 'Account Question' },
  { value: 'billing', label: 'Billing' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'legacy_issue', label: 'Legacy Issue' },
  { value: 'other', label: 'Other' },
];

export const SUPPORT_TICKET_STATUS_OPTIONS: Array<{ value: SupportTicketStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'observation', label: 'Observation' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'pending_customer', label: 'Pending Customer' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

function statusVariant(status: SupportTicketStatus) {
  switch (status) {
    case 'open':
      return 'info';
    case 'in_progress':
      return 'warning';
    case 'observation':
      return 'purple';
    case 'on_hold':
      return 'warning';
    case 'pending_customer':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'neutral';
  }
}

function priorityVariant(priority: SupportTicketPriority) {
  switch (priority) {
    case 'low':
      return 'success';
    case 'medium':
      return 'info';
    case 'high':
      return 'warning';
    case 'critical':
      return 'error';
  }
}

export function supportTicketStatusLabel(status: SupportTicketStatus) {
  return SUPPORT_TICKET_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

export function supportTicketPriorityLabel(priority: SupportTicketPriority) {
  return SUPPORT_TICKET_PRIORITY_OPTIONS.find((option) => option.value === priority)?.label ?? priority;
}

export function supportTicketTypeLabel(type: SupportTicketType | null | undefined) {
  if (!type) return 'Other';
  return SUPPORT_TICKET_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type;
}

/**
 * Resolves the assignee label for the help-center side. Super admins are
 * branded as "Prospera AI Support" so requesters never see internal staff
 * names; managers always see the real assignee.
 */
export function supportTicketAssigneeLabel(ticket: SupportTicket, managerView: boolean): string {
  if (!ticket.assignedTo) return managerView ? 'Unassigned' : 'Support queue';
  if (!managerView && ticket.assignedTo.isSuperAdmin) return 'Prospera AI Support';
  return ticket.assignedTo.displayName || 'Prospera AI Support';
}

export function SupportTicketTypeBadge({ type }: { type: SupportTicketType | null | undefined }) {
  return <Badge variant="neutral">{supportTicketTypeLabel(type)}</Badge>;
}

/**
 * Renders a ticket timestamp as the exact local date and time with the
 * relative age in parentheses, e.g. "Aug 21, 2026 3:42 PM (3 days ago)".
 */
export function formatSupportTicketDate(value: string | null) {
  if (!value) return 'Just now';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return `${format(date, 'MMM d, yyyy h:mm a')} (${formatDistanceToNow(date, { addSuffix: true })})`;
}

const URL_REGEX = /https?:\/\/[^\s<>"']+/g;
const LOOM_REGEX = /https?:\/\/(?:www\.)?loom\.com\/share\/([a-zA-Z0-9]+)(?:\?[^\s]*)?/g;
const URL_TRAILING_PUNCTUATION = /[.,;:!?)\]}>]+$/;

/**
 * Renders a free-text ticket body, turning URLs into new-tab links and
 * appending an embedded Loom player below the text whenever a Loom share
 * URL is detected. Plain segments preserve newlines via `whitespace-pre-wrap`.
 *
 * Everything is rendered as React nodes (no `dangerouslySetInnerHTML`) so
 * user-supplied bodies cannot inject markup.
 */
export function SupportMessageBody({ body, className }: { body: string; className?: string }) {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  URL_REGEX.lastIndex = 0;
  for (const match of body.matchAll(URL_REGEX)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      nodes.push(body.slice(lastIndex, start));
    }
    const raw = match[0];
    const trailing = raw.match(URL_TRAILING_PUNCTUATION)?.[0] ?? '';
    const href = trailing ? raw.slice(0, -trailing.length) : raw;
    nodes.push(
      <a
        key={`lnk-${key++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-blue-600 underline hover:text-blue-700"
      >
        {href}
      </a>,
    );
    if (trailing) {
      nodes.push(trailing);
    }
    lastIndex = start + raw.length;
  }
  if (lastIndex < body.length) {
    nodes.push(body.slice(lastIndex));
  }

  const loomIds: string[] = [];
  LOOM_REGEX.lastIndex = 0;
  for (const match of body.matchAll(LOOM_REGEX)) {
    const id = match[1];
    if (id && !loomIds.includes(id)) {
      loomIds.push(id);
    }
  }

  return (
    <div className={className}>
      <div className="whitespace-pre-wrap break-words">{nodes}</div>
      {loomIds.map((id) => (
        <div
          key={`loom-${id}`}
          className="mt-3 aspect-video w-full overflow-hidden rounded-lg border border-slate-200 bg-black"
        >
          <iframe
            src={`https://www.loom.com/embed/${id}`}
            title="Loom recording"
            allow="fullscreen; clipboard-write"
            allowFullScreen
            className="h-full w-full"
          />
        </div>
      ))}
    </div>
  );
}

export function SupportTicketStatusBadge({ status }: { status: SupportTicketStatus }) {
  return (
    <Badge variant={statusVariant(status)} dot>
      {supportTicketStatusLabel(status)}
    </Badge>
  );
}

export function SupportTicketPriorityBadge({ priority }: { priority: SupportTicketPriority }) {
  return <Badge variant={priorityVariant(priority)}>{supportTicketPriorityLabel(priority)}</Badge>;
}

interface SupportTicketComposerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (ticket: SupportTicket) => void;
  accountID?: number;
}

export function SupportTicketComposerModal({
  isOpen,
  onClose,
  onCreated,
  accountID,
}: SupportTicketComposerModalProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [subject, setSubject] = useState('');
  const [type, setType] = useState<SupportTicketType>('other');
  const [priority, setPriority] = useState<SupportTicketPriority>('medium');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const composerFileUploadRef = useRef<FileUploadHandle>(null);

  // Colleagues who can be added as the ticket is created. Support users
  // (who must pick an account) get no list here, and the field is hidden.
  const candidatesQuery = useQuery({
    queryKey: ['support-ticket-participant-candidates', 'new', accountID ?? 0],
    queryFn: () => listSupportTicketParticipantCandidates({ accountID }),
    enabled: isOpen,
    staleTime: 60_000,
    retry: false,
  });
  const candidateOptions = useMemo(
    () => buildSupportTicketParticipantOptions(candidatesQuery.data ?? []),
    [candidatesQuery.data],
  );
  const candidatesUnavailable = candidatesQuery.isError && getErrorCode(candidatesQuery.error) !== 'ACCOUNT_REQUIRED';
  const showPeoplePicker = candidateOptions.length > 0 || candidatesUnavailable;

  const createMutation = useMutation({
    mutationFn: (payload: CreateSupportTicketPayload) => createSupportTicket(payload),
    onSuccess: (ticket) => {
      toast.success('Ticket submitted', 'Your support ticket was created successfully.');
      void queryClient.invalidateQueries({ queryKey: ['support-ticket-list'] });
      void queryClient.invalidateQueries({ queryKey: ['support-ticket-summary'] });
      setSubject('');
      setType('other');
      setPriority('medium');
      setMessage('');
      setAttachments([]);
      setParticipantIds([]);
      onCreated?.(ticket);
      onClose();
    },
    onError: (error) => {
      toast.error('Unable to create ticket', getErrorMessage(error));
    },
  });

  const canSubmit = subject.trim() !== '' && message.trim() !== '';
  const handleComposerClose = () => {
    if (!createMutation.isPending) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleComposerClose}
      title="Submit Support Ticket"
      size="lg"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={handleComposerClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              createMutation.mutate({
                subject: subject.trim(),
                type,
                priority,
                message: message.trim(),
                attachments,
                accountID,
                ...(participantIds.length > 0 ? { participantUserIds: participantIds.map(Number) } : {}),
              })
            }
            disabled={!canSubmit || createMutation.isPending}
            loading={createMutation.isPending}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Create Ticket
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Describe the issue briefly"
          required
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select<SupportTicketType>
            label="Type"
            options={SUPPORT_TICKET_TYPE_OPTIONS}
            value={type}
            onChange={(value) => setType(value ?? 'other')}
          />

          <Select<SupportTicketPriority>
            label="Priority"
            options={SUPPORT_TICKET_PRIORITY_OPTIONS}
            value={priority}
            onChange={(value) => setPriority(value ?? 'medium')}
            hint={SUPPORT_TICKET_PRIORITY_DESCRIPTIONS[priority]}
          />
        </div>

        <Textarea
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onPaste={(e) => {
            const images = getImageFilesFromClipboard(e.clipboardData);
            if (images.length > 0) composerFileUploadRef.current?.addFiles(images);
          }}
          placeholder="What happened, what were you trying to do, and what should have happened instead?"
          rows={6}
          required
          showCount
          maxLength={20000}
        />

        {showPeoplePicker && (
          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-700">Add people</p>
            {candidatesUnavailable ? (
              <p className="text-sm text-slate-500">
                Couldn&apos;t load your colleagues. You can still submit, then add people from the ticket.
              </p>
            ) : (
              <>
                <SearchableMultiSelect
                  options={candidateOptions}
                  selectedValues={participantIds}
                  onSelectionChange={setParticipantIds}
                  placeholder="Choose colleagues (optional)…"
                  searchPlaceholder="Search by name or email…"
                  emptyMessage="No one matches that search."
                />
                <p className="mt-1 text-xs text-slate-500">They&apos;ll see this ticket and get its updates.</p>
              </>
            )}
          </div>
        )}

        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700">Attachments</p>
          <div className="mb-3 flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-sm text-blue-900">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="mt-0.5 h-5 w-5 shrink-0 text-blue-600">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
            </svg>
            <span>
              <strong>Uploading a screenshot?</strong> Please make sure the browser's full URL bar is visible so we can identify the exact page.
            </span>
          </div>
          <FileUpload
            ref={composerFileUploadRef}
            multiple
            maxSize={8 * 1024 * 1024}
            onChange={setAttachments}
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx"
          />
        </div>
      </div>
    </Modal>
  );
}

interface SupportTicketThreadProps {
  ticketId: number;
  managerView: boolean;
  onUpdated?: () => void;
  /**
   * Manager-side hook for navigating to the merge target when viewing a
   * merged source ticket. The admin queue wires this so clicking the banner
   * swaps the selected ticket in-panel instead of triggering a route change.
   */
  onMergedTargetClick?: (targetSupportTicketId: number) => void;
  /**
   * Shortens button/label wording for the narrow docked side panel, where
   * the full text wraps awkwardly. The full-screen modal view leaves this
   * unset to keep the descriptive labels.
   */
  compact?: boolean;
}

export function SupportTicketThread({
  ticketId,
  managerView,
  onUpdated,
  onMergedTargetClick,
  compact = false,
}: SupportTicketThreadProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SupportTicketStatus>('open');
  const [priority, setPriority] = useState<SupportTicketPriority>('medium');
  const [type, setType] = useState<SupportTicketType>('other');
  const [assignedToUserId, setAssignedToUserId] = useState<number | null>(null);
  // Whether saving a status change emails the requester. Defaults on for every
  // ticket and every save; deliberately not reset by ticket refetches, so a
  // background refresh can't re-check it out from under the user.
  const [notifyAuthor, setNotifyAuthor] = useState(true);
  // The side panel and the expanded modal can both be mounted; a label-derived
  // id would collide and toggle the wrong instance.
  const notifyAuthorId = useId();
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<number | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<SupportTicketAttachment | null>(null);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  // The text the last reply pill put in the box, and a pick waiting on a
  // confirm. A pill replaces the box outright only while the box is empty or
  // still holds the previous pick untouched, so typed text is never lost to a
  // stray click.
  const [pickedReply, setPickedReply] = useState<string | null>(null);
  const [pendingPick, setPendingPick] = useState<string | null>(null);
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const replyFileUploadRef = useRef<FileUploadHandle>(null);
  const [composerTab, setComposerTab] = useState<'reply' | 'internal'>('reply');
  const [internalMessage, setInternalMessage] = useState('');
  const [internalTargetUserId, setInternalTargetUserId] = useState<number | null>(null);
  const [internalAttachments, setInternalAttachments] = useState<File[]>([]);
  const internalFileUploadRef = useRef<FileUploadHandle>(null);

  const ticketQuery = useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => getSupportTicket(ticketId),
  });

  const ticketAccountId = ticketQuery.data?.accountId;
  const assigneesQuery = useQuery({
    queryKey: ['support-ticket-assignees', ticketAccountId ?? 0],
    queryFn: () => listSupportTicketAssignees(undefined, ticketAccountId),
    enabled: managerView && !!ticketAccountId,
  });

  const messageCount = ticketQuery.data?.messages?.length ?? 0;
  const suggestionsQuery = useQuery({
    queryKey: ['support-ticket-suggestions', ticketId, status, messageCount],
    queryFn: () => suggestSupportTicketReplies(ticketId, status),
    enabled: managerView && messageCount > 0 && !!ticketQuery.data,
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // ── "Plan with Claude" manual investigation ──────────────────────────────
  // Available only to the ticket's assignee, when the feature is on (both flags
  // come authoritatively from the show() response), and while the ticket is
  // still actionable — hidden once it is resolved or closed. Based on the
  // persisted status, not the unsaved dropdown value.
  const aiInvestigationAvailable =
    !!ticketQuery.data?.aiInvestigationAvailable &&
    !!ticketQuery.data?.viewerIsAssignee &&
    !['resolved', 'closed'].includes(ticketQuery.data?.status ?? '');

  // Watching a run's progress is broader than the assignee-only trigger: any
  // manager with the feature on can see live progress, so a manager who
  // messaged Prospera sees "Analyzing…" even if they aren't the assignee.
  const canViewInvestigation =
    !!ticketQuery.data?.prosperaMessagingAvailable || aiInvestigationAvailable;

  const [pollInvestigation, setPollInvestigation] = useState(false);
  const investigationQuery = useQuery({
    queryKey: ['support-ticket-investigation', ticketId],
    queryFn: () => getSupportTicketInvestigation(ticketId),
    enabled: canViewInvestigation,
    refetchInterval: pollInvestigation ? 2000 : false,
    refetchOnWindowFocus: false,
  });
  const investigation = investigationQuery.data ?? null;
  const isInvestigating = investigation?.status === 'pending';

  // Drive polling from the run status and surface terminal outcomes once — but
  // only for a run we actually watched finish (prev status was 'pending'), so
  // reopening a ticket with an old completed run doesn't fire a stale toast.
  const prevInvestigationStatus = useRef<string | null>(null);
  useEffect(() => {
    const current = investigation?.status ?? null;
    const prev = prevInvestigationStatus.current;
    prevInvestigationStatus.current = current;

    if (current === 'pending') {
      setPollInvestigation(true);
      return;
    }
    // current is now null | 'ready' | 'failed'.
    if (current) {
      setPollInvestigation(false);
      if (prev === 'pending') {
        if (current === 'ready') {
          toast.success('Prospera replied', 'Prospera posted an internal note on this ticket.');
          void queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
        } else if (current === 'failed') {
          toast.error('Investigation failed', investigation?.error ?? undefined);
        }
      }
    }
  }, [investigation?.status, investigation?.error, queryClient, ticketId, toast]);

  const investigateMutation = useMutation({
    mutationFn: () => investigateSupportTicket(ticketId),
    onSuccess: (run) => {
      queryClient.setQueryData(['support-ticket-investigation', ticketId], run);
      setPollInvestigation(true);
    },
    onError: (error) => {
      toast.error('Unable to start investigation', getErrorMessage(error));
    },
  });

  useEffect(() => {
    if (!ticketQuery.data) return;
    setStatus(ticketQuery.data.status);
    setPriority(ticketQuery.data.priority);
    setType(ticketQuery.data.type ?? 'other');
    setAssignedToUserId(ticketQuery.data.assignedToUserId);
  }, [ticketQuery.data]);

  useEffect(() => {
    setNotifyAuthor(true);
  }, [ticketId]);

  const applyPick = (text: string) => {
    setReplyMessage(text);
    setPickedReply(text);
  };

  const pickReply = (text: string) => {
    if (replyMessage.trim() === '' || replyMessage === pickedReply) {
      applyPick(text);
      return;
    }
    setPendingPick(text);
  };

  const updateMutation = useMutation({
    mutationFn: () =>
      updateSupportTicket(ticketId, {
        status,
        priority,
        type,
        assignedToUserId,
        notifyRequester: notifyAuthor,
      }),
    onSuccess: () => {
      toast.success('Ticket updated');
      setNotifyAuthor(true);
      void queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      void queryClient.invalidateQueries({ queryKey: ['support-ticket-list'] });
      void queryClient.invalidateQueries({ queryKey: ['support-ticket-summary'] });
      onUpdated?.();
    },
    onError: (error) => {
      toast.error('Unable to update ticket', getErrorMessage(error));
    },
  });

  const replyMutation = useMutation({
    mutationFn: (payload: ReplyToSupportTicketPayload) => replyToSupportTicket(ticketId, payload),
    onSuccess: () => {
      toast.success('Reply sent');
      setReplyMessage('');
      setReplyAttachments([]);
      void queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      void queryClient.invalidateQueries({ queryKey: ['support-ticket-list'] });
      void queryClient.invalidateQueries({ queryKey: ['support-ticket-summary'] });
      onUpdated?.();
    },
    onError: (error) => {
      toast.error('Unable to send reply', getErrorMessage(error));
    },
  });

  const internalNoteMutation = useMutation({
    mutationFn: (payload: PostInternalNotePayload) => postSupportTicketInternalNote(ticketId, payload),
    onSuccess: (result) => {
      const toProspera = !!result.investigation;
      toast.success(toProspera ? 'Sent to Prospera' : 'Internal note posted');
      setInternalMessage('');
      setInternalAttachments([]);
      setInternalTargetUserId(null);
      // A note addressed to Prospera kicks off a run — seed the query and start
      // polling so the user sees live progress and, once done, its reply.
      if (result.investigation) {
        queryClient.setQueryData(['support-ticket-investigation', ticketId], result.investigation);
        setPollInvestigation(true);
      }
      void queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      onUpdated?.();
    },
    onError: (error) => {
      toast.error('Unable to post internal note', getErrorMessage(error));
    },
  });

  const assigneeOptions = useMemo(
    () => [
      { value: 0, label: 'Unassigned' },
      ...buildSupportTicketAssigneeSelectOptions(assigneesQuery.data ?? []),
    ],
    [assigneesQuery.data],
  );

  /**
   * Internal-note recipients reuse the assignable users list, but the
   * "Unassigned" placeholder is intentionally omitted because an internal
   * note must always target a specific user who can receive the email.
   *
   * When the Prospera assistant is available, a synthetic "Prospera" recipient
   * is prepended — picking it routes the note to the assistant (which replies
   * in-thread) instead of emailing a human.
   */
  const prosperaMessagingAvailable = !!ticketQuery.data?.prosperaMessagingAvailable;
  const internalRecipientOptions = useMemo(
    () => [
      ...(prosperaMessagingAvailable
        ? [{ value: PROSPERA_RECIPIENT_ID, label: 'Prospera (AI assistant)' }]
        : []),
      ...buildSupportTicketAssigneeSelectOptions(assigneesQuery.data ?? []),
    ],
    [assigneesQuery.data, prosperaMessagingAvailable],
  );

  if (ticketQuery.isLoading) {
    return (
      <Card className="border-slate-200 bg-white">
        <CardBody className="py-10 text-center text-sm text-slate-500">Loading ticket…</CardBody>
      </Card>
    );
  }

  if (ticketQuery.isError || !ticketQuery.data) {
    return (
      <Card className="border-slate-200 bg-white">
        <CardBody className="py-10 text-center text-sm text-red-600">
          Unable to load this support ticket.
        </CardBody>
      </Card>
    );
  }

  const ticket = ticketQuery.data;
  const isMerged = !!ticket.mergedIntoSupportTicketId;
  // The API already excludes internal notes for non-managers, but filter
  // again client-side as defense in depth so a stale cache or response can
  // never leak an internal note to the requester.
  const messages = (ticket.messages ?? []).filter((m) => managerView || !m.isInternal);

  return (
    <div className="space-y-5">
      <Card className="border-slate-200 bg-white">
        <CardHeader>
          <div className="space-y-3">
            <CardTitle className="text-lg">{ticket.subject}</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <SupportTicketStatusBadge status={ticket.status} />
              <SupportTicketPriorityBadge priority={ticket.priority} />
              <SupportTicketTypeBadge type={ticket.type} />
            </div>
            <SupportTicketPeople ticket={ticket} />
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
              <span>Ticket {ticket.ticketNumber}</span>
              <span>{ticket.accountName ? `${ticket.accountName} (#${ticket.accountId})` : `Account ${ticket.accountId}`}</span>
              <span>Opened {formatSupportTicketDate(ticket.createdAt)}</span>
              {ticket.lastMessageAt && <span>Last reply {formatSupportTicketDate(ticket.lastMessageAt)}</span>}
            </div>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          {isMerged ? (
            <SupportTicketMergedBanner
              mergedIntoSupportTicketId={ticket.mergedIntoSupportTicketId}
              mergedIntoTicketNumber={ticket.mergedIntoTicketNumber}
              onMergedTargetClick={onMergedTargetClick}
            />
          ) : (
            <>
              {managerView ? (
                <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                  <Select<SupportTicketStatus>
                    label="Status"
                    value={status}
                    onChange={(value) => setStatus(value ?? 'open')}
                    options={SUPPORT_TICKET_STATUS_OPTIONS}
                  />
                  <Select<SupportTicketPriority>
                    label="Priority"
                    value={priority}
                    onChange={(value) => setPriority(value ?? 'medium')}
                    options={SUPPORT_TICKET_PRIORITY_OPTIONS}
                  />
                  <Select<SupportTicketType>
                    label="Type"
                    value={type}
                    onChange={(value) => setType(value ?? 'other')}
                    options={SUPPORT_TICKET_TYPE_OPTIONS}
                  />
                  <Select<number>
                    label="Assignee"
                    value={assignedToUserId ?? 0}
                    onChange={(value) => setAssignedToUserId((value ?? 0) > 0 ? (value as number) : null)}
                    options={assigneeOptions}
                    searchable
                  />
                </div>
              ) : (
                <div className="flex flex-wrap gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  <span>
                    Assignee:{' '}
                    <span className="font-medium text-slate-900">
                      {supportTicketAssigneeLabel(ticket, managerView)}
                    </span>
                  </span>
                  <span>
                    Unread updates:{' '}
                    <span className="font-medium text-slate-900">{ticket.viewerUnreadCount ?? ticket.requesterUnreadCount}</span>
                  </span>
                </div>
              )}

              {managerView && (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {aiInvestigationAvailable && (
                    isInvestigating ? (
                      <div className="w-full">
                        <div className="flex items-center gap-3 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-violet-50 px-4 py-2.5 shadow-sm">
                          <ProsperaSplatIcon className="h-4 w-auto shrink-0 animate-pulse text-violet-600" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-violet-800">
                              {investigation?.stage ?? 'Working…'}
                            </p>
                            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-violet-100">
                              <div className="h-full w-full origin-left rounded-full bg-gradient-to-r from-violet-400 via-fuchsia-500 to-violet-400 animate-indeterminate-progress" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mr-auto">
                        <Button
                          variant="secondary"
                          className="gap-1.5 border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 active:bg-emerald-200"
                          onClick={() => investigateMutation.mutate()}
                          loading={investigateMutation.isPending}
                          leftIcon={<ProsperaSplatIcon className="h-[13px] w-auto" />}
                        >
                          {compact ? 'Insights' : 'Prospera Insights'}
                        </Button>
                      </div>
                    )
                  )}
                  <Checkbox
                    id={notifyAuthorId}
                    label="Notify author"
                    checked={notifyAuthor}
                    onChange={(e) => setNotifyAuthor((e.target as HTMLInputElement).checked)}
                    wrapperClassName="items-center"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => setMergeModalOpen(true)}
                    leftIcon={<GitMerge className="h-4 w-4" />}
                  >
                    {compact ? 'Merge' : 'Merge Ticket'}
                  </Button>
                  <Button
                    onClick={() => updateMutation.mutate()}
                    loading={updateMutation.isPending}
                    leftIcon={<Save className="h-4 w-4" />}
                  >
                    {compact ? 'Save' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <Card className="border-slate-200 bg-white">
        <CardHeader>
          <CardTitle className="text-base">Conversation</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {messages.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
              No messages yet.
            </div>
          )}

          {messages.map((message) => {
            // Yoda's automated investigation notes get a purple treatment to set
            // them apart from human amber internal notes.
            const isYoda = message.isInternal && message.authorName === 'Yoda';
            return (
            <div
              key={message.supportTicketMessageId}
              className={
                isYoda
                  ? 'rounded-xl border border-purple-300 bg-purple-50 p-4 text-purple-900'
                  : message.isInternal
                    ? 'rounded-xl border border-amber-300 bg-amber-50 p-4'
                    : 'rounded-xl border border-slate-200 bg-slate-50 p-4'
              }
            >
              <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  {message.isInternal ? (
                    <Badge variant={isYoda ? 'purple' : 'warning'} dot>
                      <Lock className="mr-1 inline h-3 w-3" />
                      {isYoda ? 'Prospera' : 'Internal'}
                    </Badge>
                  ) : (
                    <Badge variant={message.authorRole === 'support' ? 'info' : 'neutral'} dot>
                      {message.authorRole === 'support' ? 'Support' : 'Requester'}
                    </Badge>
                  )}
                  <span className={isYoda ? 'text-sm font-medium text-purple-900' : 'text-sm font-medium text-slate-900'}>
                    {isYoda ? 'Prospera Insights' : message.authorName}
                  </span>
                  {message.authorEmail && (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                      <Mail className="h-3 w-3" />
                      {message.authorEmail}
                    </span>
                  )}
                  {message.isInternal && message.targetName && (
                    <span className={isYoda ? 'text-xs text-purple-800' : 'text-xs text-amber-800'}>to: {message.targetName}</span>
                  )}
                </div>
                <div className="text-xs text-slate-500">{formatSupportTicketDate(message.createdAt)}</div>
              </div>

              <SupportMessageBody body={message.body} className={isYoda ? 'text-sm text-purple-800' : 'text-sm text-slate-700'} />

              {message.attachments.length > 0 && (() => {
                const imageAttachments = message.attachments.filter(isSupportTicketImageAttachment);
                const otherAttachments = message.attachments.filter((a) => !isSupportTicketImageAttachment(a));
                return (
                  <div className="mt-4 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Attachments</p>

                    {imageAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {imageAttachments.map((attachment) => (
                          <SupportTicketImageThumbnail
                            key={attachment.supportTicketAttachmentId}
                            attachment={attachment}
                            onOpen={() => setPreviewAttachment(attachment)}
                          />
                        ))}
                      </div>
                    )}

                    {otherAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {otherAttachments.map((attachment) => (
                          <button
                            key={attachment.supportTicketAttachmentId}
                            type="button"
                            onClick={async () => {
                              try {
                                setDownloadingAttachmentId(attachment.supportTicketAttachmentId);
                                await downloadSupportTicketAttachment(attachment);
                              } catch (error) {
                                toast.error('Unable to download attachment', getErrorMessage(error));
                              } finally {
                                setDownloadingAttachmentId((currentId) =>
                                  currentId === attachment.supportTicketAttachmentId ? null : currentId,
                                );
                              }
                            }}
                            disabled={downloadingAttachmentId === attachment.supportTicketAttachmentId}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
                          >
                            <Paperclip className="h-3.5 w-3.5" />
                            {attachment.originalFilename}
                            <ArrowUpRight className="h-3.5 w-3.5" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            );
          })}
        </CardBody>
      </Card>

      {!isMerged && (
      <Card className="border-slate-200 bg-white">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
          {managerView ? (
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 text-sm">
              <button
                type="button"
                onClick={() => setComposerTab('reply')}
                className={
                  composerTab === 'reply'
                    ? 'rounded-md bg-white px-3 py-1.5 font-medium text-slate-900 shadow-sm'
                    : 'rounded-md px-3 py-1.5 text-slate-600 hover:text-slate-900'
                }
              >
                Reply
              </button>
              <button
                type="button"
                onClick={() => setComposerTab('internal')}
                className={
                  composerTab === 'internal'
                    ? 'inline-flex items-center gap-1 rounded-md bg-amber-100 px-3 py-1.5 font-medium text-amber-900 shadow-sm'
                    : 'inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-slate-600 hover:text-slate-900'
                }
              >
                <Lock className="h-3.5 w-3.5" />
                Internal Note
              </button>
            </div>
          ) : (
            <CardTitle className="text-base">Add Reply</CardTitle>
          )}
          {managerView && composerTab === 'reply' && (
            <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5 sm:w-auto sm:grow sm:basis-80">
              {/* Templates first, in a start-aligned group whose row is set by its
                  basis rather than its content, so they hold still while the AI
                  pills load in. */}
              <ReplyTemplatePills onPick={pickReply} />
              <SuggestedReplyPills
                isLoading={suggestionsQuery.isFetching}
                suggestions={suggestionsQuery.data?.suggestions ?? []}
                onPick={pickReply}
              />
            </div>
          )}
        </div>
        <CardBody className="space-y-4">
          {(!managerView || composerTab === 'reply') && (
            <>
              <Textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                onPaste={(e) => {
                  const images = getImageFilesFromClipboard(e.clipboardData);
                  if (images.length > 0) replyFileUploadRef.current?.addFiles(images);
                }}
                placeholder={managerView ? 'Reply to the requester…' : 'Reply to support…'}
                rows={5}
                showCount
                maxLength={20000}
              />

              <div>
                <p className="mb-1.5 text-sm font-medium text-slate-700">Attachments</p>
                <FileUpload
                  ref={replyFileUploadRef}
                  multiple
                  maxSize={8 * 1024 * 1024}
                  onChange={setReplyAttachments}
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() =>
                    replyMutation.mutate({
                      message: replyMessage.trim(),
                      attachments: replyAttachments,
                    })
                  }
                  disabled={replyMessage.trim() === '' || replyMutation.isPending}
                  loading={replyMutation.isPending}
                  leftIcon={<Send className="h-4 w-4" />}
                >
                  Send Reply
                </Button>
              </div>
            </>
          )}

          {managerView && composerTab === 'internal' && (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Internal notes are visible to support staff only. The requester does not see them, and they do not affect the
                ticket&rsquo;s last-reply timestamp or unread counters.
              </div>

              <Select<number>
                label="Notify"
                placeholder="Pick a recipient…"
                value={internalTargetUserId}
                onChange={(value) =>
                  setInternalTargetUserId(
                    value === PROSPERA_RECIPIENT_ID || (value ?? 0) > 0 ? (value as number) : null,
                  )
                }
                options={internalRecipientOptions}
                searchable
                clearable
              />

              {internalTargetUserId === PROSPERA_RECIPIENT_ID && (
                <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-900">
                  Prospera will read the ticket and reply in-thread. Ask a question, request a summary, or
                  describe what&rsquo;s broken and where.
                </div>
              )}

              <Textarea
                value={internalMessage}
                onChange={(e) => setInternalMessage(e.target.value)}
                onPaste={(e) => {
                  const images = getImageFilesFromClipboard(e.clipboardData);
                  if (images.length > 0) internalFileUploadRef.current?.addFiles(images);
                }}
                placeholder={
                  internalTargetUserId === PROSPERA_RECIPIENT_ID
                    ? 'Ask Prospera a question, or describe what to look into…'
                    : 'Leave a note for another support staff member…'
                }
                rows={5}
                showCount
                maxLength={20000}
              />

              <div>
                <p className="mb-1.5 text-sm font-medium text-slate-700">Attachments</p>
                <FileUpload
                  ref={internalFileUploadRef}
                  multiple
                  maxSize={8 * 1024 * 1024}
                  onChange={setInternalAttachments}
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    if (!internalTargetUserId) {
                      toast.error('Pick a recipient', 'Internal notes require a target user.');
                      return;
                    }
                    internalNoteMutation.mutate({
                      message: internalMessage.trim(),
                      targetUserId: internalTargetUserId,
                      attachments: internalAttachments,
                    });
                  }}
                  disabled={internalMessage.trim() === '' || !internalTargetUserId || internalNoteMutation.isPending}
                  loading={internalNoteMutation.isPending}
                  leftIcon={<Lock className="h-4 w-4" />}
                >
                  {internalTargetUserId === PROSPERA_RECIPIENT_ID ? 'Ask Prospera' : 'Post Internal Note'}
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>
      )}

      {!managerView && (
        <div className="text-xs text-slate-500">
          Need to return to the Help Center queue?{' '}
          <Link to="/help" className="font-medium text-blue-600 hover:text-blue-700">
            Go back to Help
          </Link>
        </div>
      )}

      <SupportTicketImagePreviewModal
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />

      {managerView && (
        <ConfirmDialog
          isOpen={pendingPick !== null}
          onClose={() => setPendingPick(null)}
          onConfirm={() => {
            if (pendingPick !== null) applyPick(pendingPick);
          }}
          title="Replace your reply?"
          message="What you have typed in the reply box will be replaced with this reply."
          confirmLabel="Replace"
          cancelLabel="Keep my reply"
          variant="warning"
        />
      )}

      {managerView && (
        <SupportTicketMergeModal
          isOpen={mergeModalOpen}
          onClose={() => setMergeModalOpen(false)}
          sourceTicket={ticket}
          onMerged={(targetSupportTicketId) => {
            setMergeModalOpen(false);
            onUpdated?.();
            onMergedTargetClick?.(targetSupportTicketId);
          }}
        />
      )}
    </div>
  );
}

/**
 * Banner shown at the top of a ticket detail when the ticket has been merged
 * into another. Replaces the editable controls so the source ticket stays
 * strictly read-only while still pointing the user at the target.
 */
function SupportTicketMergedBanner({
  mergedIntoSupportTicketId,
  mergedIntoTicketNumber,
  onMergedTargetClick,
}: {
  mergedIntoSupportTicketId: number | null;
  mergedIntoTicketNumber: string | null;
  onMergedTargetClick?: (targetSupportTicketId: number) => void;
}) {
  if (!mergedIntoSupportTicketId) return null;
  const label = mergedIntoTicketNumber ? mergedIntoTicketNumber : `#${mergedIntoSupportTicketId}`;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <GitMerge className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">This ticket has been merged.</p>
        <p className="mt-1 text-amber-800">
          The conversation continues on{' '}
          {onMergedTargetClick ? (
            <button
              type="button"
              onClick={() => onMergedTargetClick(mergedIntoSupportTicketId)}
              className="font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950"
            >
              {label}
            </button>
          ) : (
            <Link
              to={`/help/tickets/${mergedIntoSupportTicketId}`}
              className="font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950"
            >
              {label}
            </Link>
          )}
          . Further replies on this ticket are disabled.
        </p>
      </div>
    </div>
  );
}

/**
 * Modal for picking a same-account target ticket to merge the current ticket
 * into. Pulls a fixed page of non-closed candidates and lets the embedded
 * `Select` filter through them locally. Only used in the manager-side admin
 * queue; the requester help-center never renders this.
 */
function SupportTicketMergeModal({
  isOpen,
  onClose,
  sourceTicket,
  onMerged,
}: {
  isOpen: boolean;
  onClose: () => void;
  sourceTicket: SupportTicket;
  onMerged: (targetSupportTicketId: number) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [targetId, setTargetId] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setTargetId(null);
      setReason('');
    }
  }, [isOpen]);

  // Candidate target tickets: same account, currently active (not closed,
  // not merged elsewhere, not this ticket itself). Pulled once when the
  // modal opens; Select's searchable filter handles narrowing locally.
  const candidatesQuery = useQuery({
    queryKey: ['support-ticket-merge-candidates', sourceTicket.accountId],
    queryFn: () =>
      listSupportTickets({
        accountID: sourceTicket.accountId,
        status: 'open,in_progress,observation,on_hold,pending_customer',
        perPage: 50,
      }),
    enabled: isOpen,
    staleTime: 30_000,
  });

  const candidateOptions = useMemo(() => {
    const rows = candidatesQuery.data?.data ?? [];
    return rows
      .filter((t) => t.supportTicketId !== sourceTicket.supportTicketId)
      .filter((t) => !t.mergedIntoSupportTicketId)
      .map((t) => ({
        value: t.supportTicketId,
        label: `${t.ticketNumber} — ${t.subject}`,
      }));
  }, [candidatesQuery.data, sourceTicket.supportTicketId]);

  const mergeMutation = useMutation({
    mutationFn: (payload: MergeSupportTicketPayload) =>
      mergeSupportTicket(sourceTicket.supportTicketId, payload),
    onSuccess: (target) => {
      toast.success('Ticket merged', `${sourceTicket.ticketNumber} merged into ${target.ticketNumber}.`);
      void queryClient.invalidateQueries({ queryKey: ['support-ticket'] });
      void queryClient.invalidateQueries({ queryKey: ['support-ticket-list'] });
      void queryClient.invalidateQueries({ queryKey: ['support-ticket-summary'] });
      void queryClient.invalidateQueries({ queryKey: ['support-ticket-merge-candidates'] });
      onMerged(target.supportTicketId);
    },
    onError: (error) => {
      toast.error('Unable to merge ticket', getErrorMessage(error));
    },
  });

  const canSubmit = targetId !== null && !mergeMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!mergeMutation.isPending) onClose();
      }}
      title="Merge Ticket"
      size="lg"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={mergeMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (targetId === null) return;
              mergeMutation.mutate({
                targetSupportTicketId: targetId,
                reason: reason.trim() === '' ? undefined : reason.trim(),
              });
            }}
            disabled={!canSubmit}
            loading={mergeMutation.isPending}
            leftIcon={<GitMerge className="h-4 w-4" />}
          >
            Merge Into Selected
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <p className="font-medium text-slate-900">{sourceTicket.ticketNumber} — {sourceTicket.subject}</p>
          <p className="mt-1 text-xs text-slate-500">
            All messages and attachments on this ticket will move to the target. This ticket will be closed and locked.
          </p>
        </div>

        <Select<number>
          label="Merge into"
          placeholder={candidatesQuery.isFetching ? 'Loading candidate tickets…' : 'Search tickets in this account…'}
          value={targetId}
          onChange={(value) => setTargetId(value ?? null)}
          options={candidateOptions}
          searchable
          clearable
          hint={
            !candidatesQuery.isFetching && candidateOptions.length === 0
              ? 'No other active tickets exist for this account.'
              : undefined
          }
        />

        <Textarea
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Optional note included in the merge announcement on the target ticket."
          rows={4}
          showCount
          maxLength={2000}
        />
      </div>
    </Modal>
  );
}

/**
 * Renders an authenticated image attachment as a clickable thumbnail.
 *
 * The attachment download endpoint is bearer-protected, so a plain
 * `<img src>` tag won't work — we pull the blob through the API client and
 * expose it as an object URL via React Query (same pattern as
 * usePatientPhoto). The cache entry retains the URL for the duration of the
 * gcTime so the lightbox can render instantly from the same source.
 */
function useSupportTicketAttachmentImage(
  attachment: SupportTicketAttachment | null | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: [
      'support-ticket-attachment-image',
      attachment?.supportTicketId,
      attachment?.supportTicketAttachmentId,
    ],
    queryFn: () => fetchSupportTicketAttachmentBlobUrl(attachment!),
    enabled: enabled && !!attachment && isSupportTicketImageAttachment(attachment),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

function SupportTicketImageThumbnail({
  attachment,
  onOpen,
}: {
  attachment: SupportTicketAttachment;
  onOpen: () => void;
}) {
  const { data: src, isFetching, isError } = useSupportTicketAttachmentImage(attachment);

  return (
    <button
      type="button"
      onClick={onOpen}
      title={attachment.originalFilename}
      className="group relative h-24 w-24 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 transition hover:border-blue-300 hover:shadow"
    >
      {src ? (
        <img
          src={src}
          alt={attachment.originalFilename}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : isError ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-400">
          <ImageOff className="h-5 w-5" />
          <span className="px-1 text-[10px] leading-tight">Preview unavailable</span>
        </div>
      ) : (
        <div className="flex h-full w-full items-center justify-center text-slate-400">
          {isFetching ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
        </div>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-black/50 px-1.5 py-0.5 text-left text-[10px] text-white opacity-0 transition group-hover:opacity-100">
        {attachment.originalFilename}
      </div>
    </button>
  );
}

function SupportTicketImagePreviewModal({
  attachment,
  onClose,
}: {
  attachment: SupportTicketAttachment | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const { data: src, isFetching, isError } = useSupportTicketAttachmentImage(attachment, !!attachment);

  const handleDownload = async () => {
    if (!attachment) return;
    try {
      setDownloading(true);
      await downloadSupportTicketAttachment(attachment);
    } catch (error) {
      toast.error('Unable to download attachment', getErrorMessage(error));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={!!attachment}
      onClose={onClose}
      title={attachment?.originalFilename ?? 'Image'}
      size="xl"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={handleDownload}
            loading={downloading}
            disabled={!attachment}
            leftIcon={<Download className="h-4 w-4" />}
          >
            Download
          </Button>
        </>
      )}
    >
      <div className="flex min-h-[300px] items-center justify-center bg-slate-100 p-4">
        {src ? (
          <img
            src={src}
            alt={attachment?.originalFilename ?? 'Attachment'}
            className="max-h-[75vh] max-w-full rounded object-contain shadow"
          />
        ) : isError ? (
          <div className="flex flex-col items-center gap-2 text-slate-500">
            <ImageOff className="h-8 w-8" />
            <p className="text-sm">Unable to load this image preview.</p>
          </div>
        ) : (
          <Loader2 className={`h-8 w-8 text-slate-400 ${isFetching ? 'animate-spin' : ''}`} />
        )}
      </div>
    </Modal>
  );
}

/**
 * The Prospera splat mark used on the Ask-Insights button and its
 * in-progress indicator — the same mark as the site favicon. Fills with
 * currentColor so it reads against whichever accent color is in use.
 */
function ProsperaSplatIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 446.80114 475.75808" className={className} aria-hidden="true" fill="currentColor">
      <circle cx="227.85216" cy="226.3827" r="64.65608" />
      <circle cx="77.53543" cy="92.66218" r="44.25657" />
      <circle cx="383.87384" cy="62.9273" r="62.9273" />
      <circle cx="381.45356" cy="337.71563" r="42.52779" />
      <circle cx="248.33811" cy="423.89491" r="51.86316" />
      <circle cx="62.9273" cy="396.83963" r="62.9273" />
      <polygon points="186.10231 257.58704 87.90806 360.88117 96.20617 368.48777 196.47495 264.41569 186.10231 257.58704" />
      <polyline points="248.33811 406.96699 239.39941 262.72787 227.35608 265.75777 237.12348 407.94212" />
      <polyline points="248.33811 247.78764 364.38709 333.91233 369.83205 322.75104 254.82013 238.58426" />
      <polyline points="267.57659 198.48183 365.92135 92.58974 355.42532 85.95232 259.14048 191.02858" />
      <polyline points="207.05538 201.0461 100.18303 103.7675 93.65114 114.32952 199.68705 209.55648" />
    </svg>
  );
}

// One pill in the composer header. The template and AI pills differ only in
// icon and label; the full text is always in the hover title.
function ReplyPill({
  icon,
  label,
  text,
  onPick,
}: {
  icon: ReactNode;
  label: string;
  text: string;
  onPick: (text: string) => void;
}) {
  return (
    <button
      type="button"
      title={text}
      onClick={() => onPick(text)}
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700 transition hover:border-blue-300 hover:bg-blue-50 sm:max-w-[14rem]"
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}

// Both pill groups render bare, into the one wrapper in the composer header,
// so the pills flow and wrap as a single row.
function ReplyTemplatePills({ onPick }: { onPick: (text: string) => void }) {
  return (
    <>
      {SUPPORT_REPLY_TEMPLATES.map((template) => (
        <ReplyPill
          key={template.id}
          icon={<FileText className="h-3 w-3 shrink-0 text-slate-500" />}
          label={template.label}
          text={template.body}
          onPick={onPick}
        />
      ))}
    </>
  );
}

function SuggestedReplyPills({
  isLoading,
  suggestions,
  onPick,
}: {
  isLoading: boolean;
  suggestions: string[];
  onPick: (text: string) => void;
}) {
  if (isLoading) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
        <Sparkles className="h-3 w-3 animate-pulse" />
        <span>Generating suggestions…</span>
      </span>
    );
  }

  return (
    <>
      {suggestions.map((suggestion, index) => (
        <ReplyPill
          key={index}
          icon={<Sparkles className="h-3 w-3 shrink-0 text-blue-500" />}
          label={suggestion}
          text={suggestion}
          onPick={onPick}
        />
      ))}
    </>
  );
}

export function SupportTicketListItem({
  ticket,
  href,
  managerView,
}: {
  ticket: SupportTicket;
  href?: string;
  managerView: boolean;
}) {
  // Practice side: the viewer's own count, so a ticket a colleague already
  // read still shows as unread for you.
  const unreadCount = managerView ? ticket.supportUnreadCount : (ticket.viewerUnreadCount ?? ticket.requesterUnreadCount);

  const content = (
    <div className="block rounded-xl border border-slate-200 bg-white p-4 transition hover:border-blue-200 hover:bg-blue-50/40">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">{ticket.subject}</span>
            <SupportTicketStatusBadge status={ticket.status} />
            <SupportTicketPriorityBadge priority={ticket.priority} />
            <SupportTicketTypeBadge type={ticket.type} />
            {ticket.mergedIntoSupportTicketId && (
              <Badge variant="neutral">
                <GitMerge className="mr-1 inline h-3 w-3" />
                Merged
              </Badge>
            )}
            {unreadCount > 0 && <Badge variant="error">{unreadCount} unread</Badge>}
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
            <span>{ticket.ticketNumber}</span>
            <span className="inline-flex items-center gap-1">
              <UserRound className="h-3 w-3" />
              {ticket.requesterName}
            </span>
            <span>{ticket.accountName ? `${ticket.accountName} (#${ticket.accountId})` : `Account ${ticket.accountId}`}</span>
            {ticket.createdAt && <span>Opened {formatSupportTicketDate(ticket.createdAt)}</span>}
            {ticket.lastMessageAt && (
              <span className="inline-flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                {formatSupportTicketDate(ticket.lastMessageAt)}
              </span>
            )}
          </div>
        </div>
        <div className="text-xs text-slate-500">
          <span>{supportTicketAssigneeLabel(ticket, managerView)}</span>
        </div>
      </div>
    </div>
  );

  if (!href) {
    return content;
  }

  return <Link to={href}>{content}</Link>;
}
